import { useCallback, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Turnstile from "@/components/Turnstile";
import AuthShell from "@/components/AuthShell";
import { ShieldCheck } from "lucide-react";

export default function AdminLogin() {
  const { login, logout } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState("");
  const [resetSignal, setResetSignal] = useState(0);

  const handleToken = useCallback((t) => setToken(t), []);

  const resetWidget = () => {
    setToken("");
    setResetSignal((s) => s + 1);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = await login(form.email, form.password, { turnstile_token: token });
      if (user.role !== "admin") {
        setError("This portal is for administrators only.");
        await logout();
        resetWidget();
        return;
      }
      navigate("/admin");
    } catch (e2) {
      setError(errMsg(e2));
      resetWidget();
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      testid="admin-login-page"
      logoTestid="admin-login-logo"
      eyebrow={<><ShieldCheck size={16} className="gold-text" /> Admin Access</>}
      title={<>Admin <span className="italic gold-text">Portal</span></>}
      sub="Fleet, bookings and site management."
      footer={
        <p className="mt-8 text-base text-muted-foreground font-ui">
          Not an administrator? <Link data-testid="admin-login-back-link" to="/login" className="gold-text hover:underline">Customer sign in</Link>
        </p>
      }
    >
      <form onSubmit={submit} className="mt-10 space-y-7">
        <div>
          <Label className="font-ui text-sm tracking-wide text-muted-foreground">Admin email</Label>
          <Input data-testid="admin-login-email-input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="input-dark mt-2 h-12 text-base rounded-2xl px-4" />
        </div>
        <div>
          <Label className="font-ui text-sm tracking-wide text-muted-foreground">Password</Label>
          <Input data-testid="admin-login-password-input" type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="input-dark mt-2 h-12 text-base rounded-2xl px-4" />
        </div>
        <div>
          <Label className="font-ui text-sm tracking-wide text-muted-foreground">Security check</Label>
          <div className="mt-2">
            <Turnstile action="login" onToken={handleToken} resetSignal={resetSignal} />
          </div>
        </div>
        {error && <p data-testid="admin-login-error" className="text-base text-destructive font-ui">{error}</p>}
        <Button data-testid="admin-login-submit-button" disabled={loading || !token} className="w-full rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-ui h-12 text-base tracking-wide transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_12px_30px_rgba(199,30,46,0.35)]">
          {loading ? "Signing in…" : "Sign In to Admin"}
        </Button>
      </form>
    </AuthShell>
  );
}
